import type { SelectOption } from '../atoms';

/**
 * us-states — canonical 50 + DC US postal codes for the FE.
 *
 * Mirrors the backend source of truth `src/config/state-codes.js` (US_STATES):
 * kept as a static constant, not fetched, so a filter dropdown renders with no
 * round-trip. Used by the ParcelsList state filter (PRD 2.1); reusable by any
 * other list that filters on a `state` column.
 *
 * Sorted alphabetically so the dropdown is scannable.
 */
export const US_STATES = [
  'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL',
  'GA', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA',
  'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE',
  'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV',
  'WY',
] as const;

/** `[{value, label}]` options for a `state` filter Select — bare 2-letter code. */
export const STATE_FILTER_OPTIONS: SelectOption[] = US_STATES.map((s) => ({ value: s, label: s }));

/**
 * Options for a state PICKER on a create/edit form (RH§3 — new client / new
 * property setup). A leading blank keeps the field optional; the rest are the
 * canonical 2-letter codes.
 */
export const STATE_SELECT_OPTIONS: SelectOption[] = [
  { value: '', label: '— Select state —' },
  ...STATE_FILTER_OPTIONS,
];

/**
 * Aliases — the Properties list surface (PRD 2.2) imported these names from a
 * parallel `usStates.ts` module before the two were consolidated into this one.
 * Kept so both naming conventions resolve to the single canonical list.
 */
export const US_STATE_CODES = US_STATES;
export const US_STATE_OPTIONS: SelectOption[] = STATE_FILTER_OPTIONS;

/**
 * Full state names, keyed by postal code (same alphabetical order as US_STATES).
 *
 * Appeal stage templates are keyed by FULL NAME ("Georgia"), while a parcel
 * carries a postal code ("GA"), so resolving a parcel to its ladder needs this
 * bridge. It lives here rather than beside either caller because a partial map
 * silently breaks the ladder for any state it omits — which is exactly what a
 * four-entry copy in two files did before org-added states were possible.
 */
export const STATE_NAMES: Record<string, string> = {
  AK: 'Alaska', AL: 'Alabama', AR: 'Arkansas', AZ: 'Arizona', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DC: 'District of Columbia', DE: 'Delaware', FL: 'Florida',
  GA: 'Georgia', HI: 'Hawaii', IA: 'Iowa', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', MA: 'Massachusetts',
  MD: 'Maryland', ME: 'Maine', MI: 'Michigan', MN: 'Minnesota', MO: 'Missouri',
  MS: 'Mississippi', MT: 'Montana', NC: 'North Carolina', ND: 'North Dakota', NE: 'Nebraska',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NV: 'Nevada', NY: 'New York',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VA: 'Virginia', VT: 'Vermont', WA: 'Washington', WI: 'Wisconsin', WV: 'West Virginia',
  WY: 'Wyoming',
};

/** Options for picking a state by name — the shape stage templates are keyed in. */
export const STATE_NAME_OPTIONS: SelectOption[] = US_STATES
  .map((code) => ({ value: STATE_NAMES[code], label: `${STATE_NAMES[code]} (${code})` }))
  .sort((a, b) => String(a.value).localeCompare(String(b.value)));

/**
 * Find which of `keys` names the same state as `input`, accepting either shape.
 *
 * `keys` are stage-template keys (full names as the org typed them); `input` is
 * whatever a parcel carries — a postal code, a full name, any casing. Returns the
 * matching key untouched, or null when nothing matches.
 */
export function resolveStateKey(keys: string[], input: string | null | undefined): string | null {
  if (!input) return null;
  const raw = input.trim();
  if (!raw) return null;
  const exact = keys.find((k) => k === raw);
  if (exact) return exact;
  const target = raw.toLowerCase();
  const viaPostal = (STATE_NAMES[raw.toUpperCase()] ?? '').toLowerCase();
  return keys.find((k) => {
    const key = k.toLowerCase();
    return key === target || (!!viaPostal && key === viaPostal);
  }) ?? null;
}
